export interface TelehealthMacro {
  dot_phrase: string;
  value: string;
}

export interface TelehealthAvailableMacro extends TelehealthMacro {
  content: string;
  keywords: string;
}

export interface MacroPhrase {
  dot_phrase?: string;
  value?: string;
  content?: string;
  keywords?: string;
}

export interface MacroState {
  suggested_macros: MacroPhrase[];
  accepted: MacroPhrase[];
  added_macros: MacroPhrase[];
}

export const TELEHEALTH_SUGGESTED_MACROS: TelehealthMacro[] = [
  {
    dot_phrase: ".vvchome",
    value:
      "Visit conducted by synchronous telehealth into the (Patient's Location). Emergency services validated through e911 service availability. Patient verbal consent either confirmed or obtained. Environment surveyed and all participants identified. Provider side facility is (VA facility).",
  },
  {
    dot_phrase: ".vvcclinic",
    value:
      "Visit conducted by synchronous telehealth into the (Patient's Clinic Location). Emergency Point of Contact (POC) information obtained or validated through Facility Contact Information option. Patient verbal consent either confirmed or obtained. Environment surveyed and all participants identified. Provider side facility is (VA facility).",
  },
  {
    dot_phrase: ".vvcnolocation",
    value:
      "The patient refused to provide their location and emergency contact information and was informed about the increased risk of not receiving timely support should an emergency occur during the visit. The health care professional and the patient agreed to continue with the visit. Provider side facility is (VA facility).",
  },
];

export const TELEHEALTH_AVAILABLE_MACROS: TelehealthAvailableMacro[] =
  TELEHEALTH_SUGGESTED_MACROS.map((macro) => ({
    ...macro,
    content: macro.value,
    keywords: "telehealth virtual video visit vvc",
  }));

export const mergeTelehealthAvailableMacros = (
  macros: MacroPhrase[] = [],
  enabled = true
): MacroPhrase[] => {
  const baseMacros = Array.isArray(macros) ? macros : [];
  if (!enabled) {
    return baseMacros;
  }

  const existingDotPhrases = new Set(
    baseMacros
      .map((macro) => (typeof macro?.dot_phrase === "string" ? macro.dot_phrase.toLowerCase() : ""))
      .filter(Boolean)
  );

  return [
    ...baseMacros,
    ...TELEHEALTH_AVAILABLE_MACROS.filter(
      (macro) => !existingDotPhrases.has(macro.dot_phrase.toLowerCase())
    ),
  ];
};

export const normalizeMacroState = (macros?: Partial<MacroState> | null): MacroState => {
  const suggestedMacros = macros?.suggested_macros;
  const accepted = macros?.accepted;
  const addedMacros = macros?.added_macros;

  return {
    suggested_macros: Array.isArray(suggestedMacros) ? suggestedMacros : [],
    accepted: Array.isArray(accepted) ? accepted : [],
    added_macros: Array.isArray(addedMacros) ? addedMacros : [],
  };
};

export const mergeTelehealthSuggestedMacros = (macros?: Partial<MacroState> | null): MacroState => {
  const base = normalizeMacroState(macros);
  const existingDotPhrases = new Set(
    [...base.suggested_macros, ...base.accepted, ...base.added_macros]
      .map((macro) => (typeof macro?.dot_phrase === "string" ? macro.dot_phrase.toLowerCase() : ""))
      .filter(Boolean)
  );
  const telehealthSuggestionsToAdd = TELEHEALTH_SUGGESTED_MACROS.filter(
    (macro) => !existingDotPhrases.has(macro.dot_phrase.toLowerCase())
  );

  return {
    ...base,
    suggested_macros: [...base.suggested_macros, ...telehealthSuggestionsToAdd],
  };
};
